/*
 * "Share on LinkedIn": opens LinkedIn's post composer with a ready-to-post
 * message, so learners don't have to think about what to write. The link in
 * the text gets LinkedIn's preview card (the API serves the Open Graph tags
 * and image for /q, /c, /e and /verify pages).
 *
 * LinkedIn only turns a mention into a tag when the person picks it from its
 * suggestions, so "@Nirvik AI Labs" arrives as plain text; LINKEDIN_TAG_TIP
 * tells them how to make it a tag.
 */

export const NIRVIK_LINKEDIN = 'https://www.linkedin.com/company/nirvik-ai-labs';
const MENTION = '@Nirvik AI Labs';
const TAGS = '#NirvikReady #CyberSecurity #Upskilling';

export const LINKEDIN_TAG_TIP =
  'Tip: to tag Nirvik AI Labs, retype “@Nirvik AI Labs” in the post and pick our page from the list.';

/** LinkedIn's composer with `text` filled in. */
export const linkedInPostUrl = (text: string) =>
  `https://www.linkedin.com/feed/?${new URLSearchParams({ shareActive: 'true', text })}`;

export type ShareSubject =
  | { kind: 'quiz'; title: string; url: string; moment?: 'passed' | 'took' }
  | { kind: 'course'; title: string; url: string }
  | { kind: 'workshop' | 'webinar'; title: string; url: string }
  | { kind: 'certificate'; of: 'quiz' | 'course' | 'event'; title: string; url: string }
  | { kind: 'org'; title: string; url: string };

/** The post for a quiz, course, event or certificate. */
export function linkedInPostText(s: ShareSubject): string {
  const title = `“${s.title}”`;
  const lines = (() => {
    switch (s.kind) {
      case 'quiz':
        if (s.moment === 'passed')
          return [
            `✅ Just passed ${title} on Nirvik Ready! 🛡️`,
            'Quick, free and surprisingly eye-opening. Most of us are one convincing message away from a bad day online, so it’s worth a few minutes to check.',
            `Think you can beat my score? Take it here 👇\n${s.url}`,
          ];
        if (s.moment === 'took')
          return [
            `🛡️ Just put my cyber smarts to the test with ${title} on Nirvik Ready.`,
            'Learned a few things I didn’t know I didn’t know. It’s free and takes only a few minutes.',
            `Your turn 👇\n${s.url}`,
          ];
        return [
          `🛡️ How cyber-ready are you, really? Find out with ${title} on Nirvik Ready.`,
          'It’s free, quick, and you’ll come away sharper than you started.',
          `Take the challenge 👇\n${s.url}`,
        ];
      case 'course':
        return [
          `📚 Leveling up with ${title}, a free course on Nirvik Ready.`,
          'Practical, bite-sized lessons you can finish on your phone, with a certificate at the end.',
          `Learn with me 👇\n${s.url}`,
        ];
      case 'workshop':
      case 'webinar':
        return [
          `📅 Save your seat: ${title}, a free live ${s.kind} on Nirvik Ready.`,
          'Learn live, ask your questions, and walk away with skills you can use the same day.',
          `Join me 👇\n${s.url}`,
        ];
      case 'org':
        return [
          `🏛️ ${s.title} is on Nirvik Ready!`,
          'Free courses, challenges and live events to build real cyber readiness, with certificates to show for it.',
          `See what they offer 👇\n${s.url}`,
        ];
      case 'certificate':
        return [
          s.of === 'event'
            ? `🎉 Proud to have taken part in ${title} on Nirvik Ready!`
            : `🎉 Certified! I just earned my certificate for ${title} on Nirvik Ready.`,
          s.of === 'course'
            ? 'Every lesson done. Staying sharp in a world where threats move fast.'
            : s.of === 'event'
              ? 'Great sessions, practical takeaways, and a certificate of participation to show for it.'
              : 'Staying sharp in a world where threats move fast, one challenge at a time.',
          `Verify it or earn your own 👇\n${s.url}`,
        ];
    }
  })();
  return [
    ...lines,
    `Thanks ${MENTION} for making cyber and AI skills free for everyone.`,
    TAGS,
  ].join('\n\n');
}
